/* script */
//
//
//
//
//
//
//
//
//
//
//
//

// Utils
import { omit, isEqual, isNil } from 'lodash';

// Components
import BaseControl from 'shared/ui/components/form-components/BaseControl.vue';
import ExternalPicker from 'shared/ui/components/form-components/pickers/ExternalPicker.vue';

const __vue_script__ = {
    name: 'MeetingLocationPicker',

    components: { ExternalPicker },

    extends: BaseControl,

    props: {
        value: {
            type: String,
            default: null
        },

        edit: {
            type: Boolean,
            default: false
        },

        model: {
            type: Object,
            default: () => ({})
        },

        options: {
            type: Object,
            default: () => ({})
        }
    },

    data() {
        return {
            attrs: {},
            disablePicker: false,
            loading: false,
            locationsList: [],
            hasLocations: false,
            locationAvailibilityProps: {},
        };
    },

    computed: {
        innerValue: {
            get: function() {
                return this.value;
            },

            set(value) {
                this.$emit('input', value);
            }
        },

        hasAllNecessaryData() {
            const { start_ts, duration, organizer, participants, externals } = this.model;
            const hasBasicInfo = Boolean(start_ts && duration && organizer?._id);
            const hasPax = Boolean(participants?.length || externals?.length);
            return hasBasicInfo && hasPax;
        },

        disableControl() {
            return this.disablePicker || this.disabled || !this.hasAllNecessaryData;
        }
    },

    watch: {
        $attrs: {
            immediate: true,
            handler() {
                this.attrs = omit({ ...this.$options.propsData, ...this.$attrs }, 'model', 'options');
                this.attrs['allow-empty'] = true;
                this.attrs['hide-selected'] = false;
            }
        },
    },

    created() {
        if (this.options.exclusive_with) {
            this.$watch(`model.${this.options.exclusive_with}`, (...args) => this.exclusivityWatcher(...args));
        }

        if (this.options.reacts_to?.length) {
            for (const reactsTo of this.options.reacts_to) {
                this.$watch(`model.${reactsTo}`,
                    (...args) => {
                        const [newValue, oldValue] = args;

                        if (!isEqual(newValue, oldValue) && !isNil(newValue)) {
                            return this.reactsToWatcher(reactsTo, ...args);
                        }
                    },
                    { deep: true });
            }
        }

        if (this.value?.length || this.hasAllNecessaryData) {
            this.getMeetingLocations();
        }
    },

    methods: {
        getLocationAvailabilityProps() {
            const timestamp = this.$utils.time.tsToFormat(this.model.start_ts, 'X');

            // remove the organizer from the list of participants (as we add one when computing the capacity)
            // We need to do that, because the organizer is NOT part of this list when an event is created
            // So the code below works in all cases
            const invitedUsers = this.model.participants.filter(p => p._id && p._id !== this.model.organizer._id);
            const paxCount = invitedUsers.length + 1 + (this.model.externals?.length || 0);

            return {
                timestamp,
                duration: parseInt(this.model.duration, 10),
                meetingId: this.model._id,
                capacity: paxCount,
            };
        },

        /** @return {boolean} */
        hasLocationAvailibilityPropsChanged() {
            const changed = !isEqual(this.locationAvailibilityProps, this.getLocationAvailabilityProps());
            if (changed) {
                this.locationAvailibilityProps = this.getLocationAvailabilityProps();
            }
            return changed;
        },

        exclusivityWatcher(newValue) {
            if (Boolean(newValue) && newValue?.length) {
                this.innerValue = null;
                this.disablePicker = true;
            } else {
                this.disablePicker = false;
            }
        },

        reactsToWatcher() {
            this.getMeetingLocations();
        },

        async getMeetingLocations() {
            try {
                if (!this.hasAllNecessaryData && !this.value?.length) return;

                if (!this.hasAllNecessaryData) {
                    // when opened in a location column of the calendar,
                    // the location is preset but missing the location doc
                    // as this is only fetched when it has "necessary data"
                    // we need to fetch it, otherwise only the _id is shown in the picker
                    const location = await this.$services.documents.getDocById(this.event._id, this.value)
                        .catch(() => undefined);
                    if (location?.fp_type === 'meeting_location' && location?.title) {
                        this.locationsList = [{ ...location, label: location.title }];
                    }
                    return;
                }

                if (!this.hasLocationAvailibilityPropsChanged()) return;

                this.loading = true;

                const { availableRooms } = await this.$services.appScripts.callAppScript(
                    this.event._id,
                    'actions/check-location-availability',
                    this.locationAvailibilityProps,
                );

                this.hasLocations = Boolean(availableRooms?.length);
                this.locationsList = availableRooms.map(item => {
                    item.label = item.description ? `${item.title} - ${item.description}` : item.title;
                    return item;
                });

                const isCurrentSelectionOnList = this.locationsList.find((item) => item._id === this.innerValue);
                if (!isCurrentSelectionOnList) {
                    this.innerValue = null;
                }

            } catch (err) {
                console.error('[MeetingLocationPicker] Error fetching meeting locations', err);
            } finally {
                this.loading = false;
            }
        }
    }

};

/* template */
var __vue_render__ = function () {var _vm=this;var _h=_vm.$createElement;var _c=_vm._self._c||_h;return _c('external-picker',_vm._b({staticClass:"meeting-location-picker",attrs:{"emit-key":"_id","fp-type":"meeting_location","name":_vm.name,"disabled":_vm.disableControl,"options":_vm.locationsList,"errors":_vm.errors,"loading":_vm.loading},model:{value:(_vm.innerValue),callback:function ($$v) {_vm.innerValue=$$v},expression:"innerValue"}},'external-picker',_vm.attrs,false))}
var __vue_staticRenderFns__ = []

  /* style */
  const __vue_inject_styles__ = undefined
  /* scoped */
  const __vue_scope_id__ = undefined
  /* module identifier */
  const __vue_module_identifier__ = undefined
  /* functional template */
  const __vue_is_functional_template__ = false
  /* component normalizer */
  function __vue_normalize__(
    template, style, script,
    scope, functional, moduleIdentifier, shadowMode,
    createInjector, createInjectorSSR, createInjectorShadow
  ) {
    const component = (typeof script === 'function' ? script.options : script) || {}

    // For security concerns, we use only base name in production mode.
    component.__file = "MeetingLocationPicker.vue"

    if (!component.render) {
      component.render = template.render
      component.staticRenderFns = template.staticRenderFns
      component._compiled = true

      if (functional) component.functional = true
    }

    component._scopeId = scope

    if (false) {
      let hook
      if (false) {
        // In SSR.
        hook = function(context) {
          // 2.3 injection
          context =
            context || // cached call
            (this.$vnode && this.$vnode.ssrContext) || // stateful
            (this.parent && this.parent.$vnode && this.parent.$vnode.ssrContext) // functional
          // 2.2 with runInNewContext: true
          if (!context && typeof __VUE_SSR_CONTEXT__ !== 'undefined') {
            context = __VUE_SSR_CONTEXT__
          }
          // inject component styles
          if (style) {
            style.call(this, createInjectorSSR(context))
          }
          // register component module identifier for async chunk inference
          if (context && context._registeredComponents) {
            context._registeredComponents.add(moduleIdentifier)
          }
        }
        // used by ssr in case component is cached and beforeCreate
        // never gets called
        component._ssrRegister = hook
      }
      else if (style) {
        hook = shadowMode 
          ? function(context) {
              style.call(this, createInjectorShadow(context, this.$root.$options.shadowRoot))
            }
          : function(context) {
              style.call(this, createInjector(context))
            }
      }

      if (hook !== undefined) {
        if (component.functional) {
          // register for functional component in vue file
          const originalRender = component.render
          component.render = function renderWithStyleInjection(h, context) {
            hook.call(context)
            return originalRender(h, context)
          }
        } else {
          // inject component registration as beforeCreate hook
          const existing = component.beforeCreate
          component.beforeCreate = existing ? [].concat(existing, hook) : [hook]
        }
      }
    }

    return component
  }
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__ = /*#__PURE__*/__vue_normalize__(
    { render: __vue_render__, staticRenderFns: __vue_staticRenderFns__ },
    __vue_inject_styles__,
    __vue_script__,
    __vue_scope_id__,
    __vue_is_functional_template__,
    __vue_module_identifier__,
    false,
    undefined,
    undefined,
    undefined
  )

  export default __vue_component__